import { useEffect, useState, useCallback } from 'react';
import { useAbortController } from './useAbortController';
import { useApiFetch } from './useApiFetch';

export interface EvalSetItem {
  eval_set_id: string;
  created_at: string;
  eval_count: number;
  latest_eval_created_at: string;
  task_names: string[];
  created_by: string | null;
  created_by_email: string | null;
  model_groups: string[];
}

interface EvalSetsResponse {
  items: EvalSetItem[];
  total: number;
  page: number;
  limit: number;
}

interface UseEvalSetsOptions {
  page?: number;
  limit?: number;
  search?: string;
}

interface UseEvalSetsResult {
  evalSets: EvalSetItem[];
  isLoading: boolean;
  error: Error | null;
  total: number;
  page: number;
  limit: number;
  setPage: (page: number) => void;
  setSearch: (search: string) => void;
  setLimit: (limit: number) => void;
  refetch: () => void;
}

export function useEvalSets(
  options: UseEvalSetsOptions = {}
): UseEvalSetsResult {
  const {
    page: initialPage = 1,
    limit: initialLimit = 50,
    search: initialSearch = '',
  } = options;

  const [evalSets, setEvalSets] = useState<EvalSetItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [search, setSearch] = useState(initialSearch);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const { error, apiFetch } = useApiFetch();
  const { getAbortController } = useAbortController();

  const refetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    const fetchEvalSets = async () => {
      setIsDataLoading(true);
      const abortController = getAbortController();

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (search && search.trim()) {
        params.append('search', search.trim());
      }

      const response = await apiFetch(`/meta/eval-sets?${params}`, {
        signal: abortController.signal,
      });

      if (!response) {
        if (!abortController.signal.aborted) {
          setIsDataLoading(false);
        }
        return;
      }

      const data: EvalSetsResponse = await response.json();

      setEvalSets(data.items);
      setTotal(data.total);
      setIsDataLoading(false);
    };

    fetchEvalSets();
  }, [page, limit, search, refetchTrigger, apiFetch, getAbortController]);

  return {
    evalSets,
    isLoading: isDataLoading,
    error,
    total,
    page,
    limit,
    setPage,
    setSearch,
    setLimit,
    refetch,
  };
}
